import { RefreshCcw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { getReportSection } from "~/api/custom-reports/custom-reports";
import { useGetOrganization } from "~/api/organizations/organizations";
import { MarkdownContentModal } from "~/components/MarkdownContentModal";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { useParentCase } from "~/features/case/hooks/useParentCase";
import { formatCaseInfo } from "~/features/chat/helpers";
import { useChatDocumentParams } from "~/helpers";
import { useMobile } from "~/hooks/use-mobile";
import { useDocumentSearchParams } from "~/hooks/useDocumentSearchParams";
import { trackEvent } from "~/utils/analytics";
import { normalizeLiteralNewlines } from "~/utils/markdownTerms";
import { getFileTitle } from "../../helpers";
import { useBatesLinks } from "../../hooks/useBatesLinks";
import { useSectionLinks } from "../../hooks/useSectionLinks";
import { GenerateReportView } from "./GenerateReportView";
import { RegenerateReportModal } from "./RegenerateReportModal";
import { ReportActions } from "./ReportActions";
import { ReportView, ReportViewStatus } from "./ReportView";
import {
  OutdatedReason,
  TemplateReportItem,
  TemplateReportSelector,
  TemplateReportStatus,
} from "./TemplateReportSelector";
import { useReportData } from "./useReportData";
import { useReportTerms } from "./useReportTerms";

export const ReportArea = () => {
  const { formatMessage } = useIntl();
  const [maximizeModalOpen, setMaximizeModalOpen] = useState(false);
  const [reportToRegenerate, setReportToRegenerate] =
    useState<TemplateReportItem | null>(null);
  const { organizationId, caseId, chatId, isGuest } = useChatDocumentParams();
  const [, updateDocumentParams] = useDocumentSearchParams();
  const { isMobile } = useMobile();

  // Get case info for PDF exports
  const { project } = useParentCase();
  const caseInfo = useMemo(
    () => (project ? formatCaseInfo(project) : undefined),
    [project]
  );

  // Get organization for Output Language setting
  const { data: organization } = useGetOrganization(organizationId);

  const terms = useReportTerms(caseId);
  const sectionLinks = useSectionLinks();
  const batesLinks = useBatesLinks();

  const {
    content,
    reportSections,
    templateReports,
    handleGenerateReport,
    isLoading,
    selectedReportIndex,
    setSelectedReportIndex,
    chatTitle,
    locallyGenerating,
    reportId,
  } = useReportData(organizationId, caseId, chatId);

  const selectedTemplate =
    templateReports.length > selectedReportIndex
      ? templateReports[selectedReportIndex].template
      : null;
  const selectedReport =
    templateReports.length > selectedReportIndex
      ? templateReports[selectedReportIndex].report
      : null;

  const isLocallyGeneratingThisTemplate =
    selectedTemplate != null && locallyGenerating.has(selectedTemplate.id);

  const isGenerateView =
    !isLoading &&
    selectedTemplate &&
    !selectedReport &&
    !isLocallyGeneratingThisTemplate &&
    project !== undefined;
  const isGenerating = selectedReport !== null;

  const exportFileName = useMemo(
    () =>
      `${selectedTemplate ? selectedTemplate.name : selectedReport?.title} ${chatTitle ? getFileTitle(chatTitle) : ""}`,
    [selectedTemplate, selectedReport?.title, chatTitle]
  );

  const [maximizeContent, setMaximizeContent] = useState("");
  const fetchControllerRef = useRef<AbortController | null>(null);

  const fetchFullContent = useCallback(async () => {
    fetchControllerRef.current?.abort();
    const controller = new AbortController();
    fetchControllerRef.current = controller;

    const completedSections = reportSections?.filter(
      (s) => s.status === "completed"
    );

    try {
      if (completedSections && completedSections.length > 0) {
        const sections = await Promise.all(
          completedSections.map((s) =>
            getReportSection(organizationId, s.id, undefined, controller.signal)
          )
        );

        if (controller.signal.aborted) return;

        const assembled = sections
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map(
            (section) =>
              `## ${section.title}\n\n${normalizeLiteralNewlines(section.content ?? "")}`
          )
          .join("\n\n");

        setMaximizeContent(assembled);
        return;
      }

      setMaximizeContent(normalizeLiteralNewlines(content));
    } catch (e) {
      if (controller.signal.aborted) return;
      setMaximizeContent(normalizeLiteralNewlines(content));
    }
  }, [organizationId, reportSections, content]);

  useEffect(() => {
    if (maximizeModalOpen) {
      fetchFullContent();
    } else {
      setMaximizeContent("");
      fetchControllerRef.current?.abort();
    }
  }, [maximizeModalOpen, fetchFullContent]);

  const fullContent = useMemo(
    () => normalizeLiteralNewlines(content),
    [content]
  );

  const handleSectionClick = useCallback(
    (sectionId: string, firstPageId: string) => {
      updateDocumentParams({
        sectionId,
        pageId: firstPageId,
      });
    },
    [updateDocumentParams]
  );

  const handleBatesClick = useCallback(
    (pageId: string) => {
      updateDocumentParams({ pageId });
    },
    [updateDocumentParams]
  );

  const templateReportItems: TemplateReportItem[] = templateReports.map(
    (templateReport, index) => {
      const { template, report } = templateReport;

      const id = template?.id ?? index.toString();
      const name = template?.name ?? report?.title ?? "";

      let status: TemplateReportStatus = "failed";
      let isOutdated = false;
      let outdatedReason: OutdatedReason | undefined = undefined;

      if (template) {
        status = "notStarted";
        if (report) {
          switch (report.status) {
            case "completed":
              status = "completed";
              break;
            case "reportProcessing":
              status = "inProgress";
              break;
            case "caseProcessing":
              if (locallyGenerating.has(template.id)) {
                status = "inProgress";
              } else {
                status = "pending";
              }
              break;
            case "outdatedByDoclist":
              status = "completed";
              isOutdated = true;
              outdatedReason = "outdatedByDoclist";
              break;
            case "outdatedByTemplate":
              status = "completed";
              isOutdated = true;
              outdatedReason = "outdatedByTemplate";
              break;
            case "failed":
              status = "failed";
              break;
            default:
              status = "failed";
              break;
          }
        }
        if (locallyGenerating.has(template.id)) {
          status = "inProgress";
        }
      }

      return {
        id,
        name,
        status,
        isOutdated,
        outdatedReason,
      };
    }
  );

  // Check if any report has caseProcessing status (means case is being reprocessed)
  const isCaseProcessing = templateReports.some(
    (tr) => tr.report?.status === "caseProcessing"
  );

  const inProgress =
    (templateReportItems.length > selectedReportIndex &&
      templateReportItems[selectedReportIndex].status === "inProgress") ||
    isLocallyGeneratingThisTemplate;
  const isPending =
    templateReportItems.length > selectedReportIndex &&
    templateReportItems[selectedReportIndex].status === "pending";

  const showActions = !isGenerateView && !inProgress && !isPending;

  const reportViewStatus: ReportViewStatus = useMemo(() => {
    if (inProgress) {
      return { type: "processing" };
    }
    if (isPending) {
      return { type: "pending" };
    }
    if (
      templateReportItems.length > selectedReportIndex &&
      templateReportItems[selectedReportIndex].isOutdated
    ) {
      return {
        type: "outdated",
        reason: templateReportItems[selectedReportIndex].outdatedReason,
      };
    }
    return { type: "ready" };
  }, [inProgress, isPending, templateReportItems, selectedReportIndex]);

  const handleConfirmRegenerate = () => {
    if (!reportToRegenerate) return;
    trackEvent({
      eventName: "app/report/regenerate.button/click",
      properties: {
        templateId: reportToRegenerate.id,
        reportName: reportToRegenerate.name,
      },
    });
    handleGenerateReport(reportToRegenerate.id);
    setReportToRegenerate(null);
  };

  return (
    <>
      <div
        className={`relative flex flex-col ${isMobile ? "h-docsidebar-content-mobile" : "h-[calc(100vh-60px)]"}`}
      >
        <div className="px-3 pt-3 z-10 bg-background shrink-0">
          {!isGuest && (
            <TemplateReportSelector
              templateReportItems={templateReportItems}
              selectedTemplateReportIndex={selectedReportIndex}
              onValueChange={setSelectedReportIndex}
              onRegenerateClick={setReportToRegenerate}
            />
          )}
          {!isGuest && reportViewStatus.type === "outdated" && (
            <Alert
              variant="default"
              className="border-amber-200 bg-amber-50 px-3 py-1.5 mb-3"
            >
              <div className="flex items-center gap-2">
                <AlertDescription className="flex-1 text-amber-800 text-xs">
                  {reportViewStatus.reason === "outdatedByDoclist"
                    ? formatMessage({
                        id: "chat.report.outdatedByDoclistAlert",
                      })
                    : reportViewStatus.reason === "outdatedByTemplate"
                      ? formatMessage({
                          id: "chat.report.outdatedByTemplateAlert",
                        })
                      : formatMessage({ id: "chat.report.outdatedAlert" })}
                </AlertDescription>
                {selectedTemplate && (
                  <Button
                    variant="ghost"
                    size="mini"
                    className="shrink-0 text-amber-700 hover:bg-amber-100 hover:text-amber-900 gap-1"
                    onClick={() => handleGenerateReport(selectedTemplate.id)}
                    disabled={inProgress}
                  >
                    <RefreshCcw className="h-3 w-3" />
                    {formatMessage({ id: "chat.report.rewriteNow" })}
                  </Button>
                )}
              </div>
            </Alert>
          )}
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 pb-3">
          {isGenerateView ? (
            <div className="px-3 flex items-center justify-center h-full">
              <GenerateReportView
                template={selectedTemplate}
                onGenerate={handleGenerateReport}
                isGenerating={isGenerating}
                isPending={isCaseProcessing}
              />
            </div>
          ) : (
            <ReportView
              reportSections={reportSections ?? []}
              status={reportViewStatus}
              terms={terms}
              sectionLinks={sectionLinks}
              content={content}
              reportId={reportId}
              templateName={selectedTemplate?.name}
              outputLanguage={organization?.language}
              isActionsAvailable={!isGuest}
              onSectionClick={handleSectionClick}
              batesLinks={batesLinks}
              onBatesClick={handleBatesClick}
            />
          )}
        </div>
        <p className="text-center text-[10px] text-muted-foreground py-1 shrink-0 whitespace-nowrap">
          <FormattedMessage id="aiDisclaimer" />
        </p>
        {showActions && (
          <div className={isMobile ? "flex-shrink-0" : ""}>
            <ReportActions
              disabled={false}
              content={fullContent}
              reportId={selectedReport?.id}
              reportSections={reportSections}
              exportFileName={exportFileName}
              reportTitle={
                selectedTemplate ? selectedTemplate.name : selectedReport?.title
              }
              caseInfo={caseInfo}
              onMaximize={() => {
                trackEvent({ eventName: "app/report/maximize.button/click" });
                setMaximizeModalOpen(true);
              }}
            />
          </div>
        )}
      </div>
      {maximizeModalOpen && (
        <MarkdownContentModal
          title={formatMessage({ id: `chat.report.title` })}
          filename={exportFileName}
          content={maximizeContent}
          open={maximizeModalOpen}
          onOpenChange={setMaximizeModalOpen}
          copyEventName={`app/report_maximize_modal/copy_all.button/click`}
          exportOpenEventName={`app/report_maximize_modal/export.button/click`}
          exportEventName={`app/report_maximize_modal/export_menu_open/export_action.link/click`}
          terms={terms}
          caseInfo={caseInfo}
          sectionLinks={sectionLinks}
          onSectionClick={handleSectionClick}
          batesLinks={batesLinks}
          onBatesClick={handleBatesClick}
          reportId={selectedReport?.id}
          reportSections={reportSections}
        />
      )}
      <RegenerateReportModal
        isOpen={reportToRegenerate !== null}
        onClose={() => setReportToRegenerate(null)}
        onConfirm={handleConfirmRegenerate}
        reportName={
          reportToRegenerate?.name ??
          (templateReportItems.length > selectedReportIndex
            ? templateReportItems[selectedReportIndex].name
            : "")
        }
      />
    </>
  );
};
